import {DataTable} from "../DataTable/DataTable";
import {TtaMappedPairEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";
import {useTicketCompareDataTableFilter} from "./useTicketCompareDataTableFilter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {getCompareColumn} from "./columns/CompareColumn/CompareColumn";
import {FormattedCurrency, getFormattedCurrency} from "../../text/FormattedCurrency/FormattedCurrency";
import {Tooltip} from "../../tooltip/Tooltip/Tooltip";
import {ActionIcon, Group, Stack, Text} from "@mantine/core";
import {FormattedQuantity, getFormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {IconLayersDifference} from "@tabler/icons-react";
import {useMemo, useRef, useState} from "react";
import {TicketCompareModal} from "../../modal/TicketCompareModal/TicketCompareModal";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {DataTableColumn} from "mantine-datatable";

const options: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 5
}

const QuantityCell = (props: { dbValue?: number, importValue?: number }) => {
  const {dbValue, importValue} = props

  const {t} = useTranslation(["common"])

  const delta = useMemo(() => {
    return (importValue ?? 0) - (dbValue ?? 0)
  }, [importValue, dbValue])

  return (
    <Tooltip label={
      <Stack>
        <Text>
          {t("common.ticket.compare.mapped", "Mapped")}
          {": "}
          {(typeof importValue !== 'undefined' ? getFormattedQuantity(importValue, undefined, options) : t("common.ticket.compare.empty", "Not Defined"))}
        </Text>
        <Text>
          {t("common.ticket.compare.internal", "Internal")}
          {": "}
          {(typeof dbValue !== 'undefined' ? getFormattedQuantity(dbValue, undefined, options) : t("common.ticket.compare.empty", "Not Defined"))}
        </Text>
        <Text>
          {t("common.ticket.compare.delta", "Delta")}
          {": "}
          {getFormattedQuantity(delta, undefined, options)}
        </Text>
      </Stack>
    }>
      <FormattedQuantity
        value={importValue}
        options={options}/>
    </Tooltip>
  )
}

const CurrencyCell = (props: { dbValue?: number, importValue?: number }) => {
  const {dbValue, importValue} = props

  const {t} = useTranslation(["common"])

  const delta = useMemo(() => {
    return (importValue ?? 0) - (dbValue ?? 0)
  }, [importValue, dbValue])

  return (
    <Tooltip label={
      <Stack>
        <Text>
          {t("common.ticket.compare.mapped", "Mapped")}
          {": "}
          {(typeof importValue !== 'undefined' ? getFormattedCurrency(importValue, options) : t("common.ticket.compare.empty", "Not Defined"))}
        </Text>
        <Text>
          {t("common.ticket.compare.internal", "Internal")}
          {": "}
          {(typeof dbValue !== 'undefined' ? getFormattedCurrency(dbValue, options) : t("common.ticket.compare.empty", "Not Defined"))}
        </Text>
        <Text>
          {t("common.ticket.compare.delta", "Delta")}
          {": "}
          {getFormattedCurrency(delta, options)}
        </Text>
      </Stack>
    }>
      <FormattedCurrency
        value={importValue}
        options={options}/>
    </Tooltip>
  )
}

export type TicketCompareDataTableProps = EntityDataTableProps<TtaMappedPairEntity>

const EMPTY_ARRAY: TtaMappedPairEntity[] = []

export const TicketCompareDataTable = (props: TicketCompareDataTableProps) => {
  const {
    records: tickets = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const [compare, setCompare] = useState<TtaMappedPairEntity | undefined>(undefined)

  const {filteredRecords, filterConfig} = useTicketCompareDataTableFilter({
    records: tickets
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'startDttm', direction: 'asc'}
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns: DataTableColumn<TtaMappedPairEntity>[] = useMemo(() => {
    return [
      getCompareColumn({
        ...getColumnFilterProps(filterConfig, "ticketNumber"),
        title: t("table.entity.ticket.column.ticketnumber", "Ticket #"),
        accessor: "ticketNumber",
        width: 100,
        isEqual: (record) => record.tta?.pickupTransport?.ticketNumber === record.dbTta?.pickupTransport?.ticketNumber,
        render: ({tta}) => {
          return tta?.pickupTransport?.ticketNumber
        },
        footer: (
          <FormattedQuantity
            options={{
              signDisplay: "exceptZero"
            }}
            value={filteredRecords.reduce((prev, curr) => prev + (curr.dbTta ? 0 : 1), 0)}
          />
        ),
      }),
      getCompareColumn({
        title: t("table.entity.ticket.column.pickupquantity", "Pickup Qty"),
        accessor: "pickupQty",
        width: 100,
        isEqual: (record) => record.tta?.pickupTransport?.quantityDecimal === record.dbTta?.pickupTransport?.quantityDecimal,
        render: ({tta, dbTta}) => {
          return (
            <QuantityCell
              importValue={tta?.pickupTransport?.quantityDecimal}
              dbValue={dbTta?.pickupTransport?.quantityDecimal}/>
          )
        },
        footer: (
          <FormattedQuantity
            options={{
              ...options,
              signDisplay: "always"
            }}
            value={filteredRecords.reduce((prev, curr) => prev + (curr.tta?.pickupTransport?.quantityDecimal ?? 0), 0) - filteredRecords.reduce((prev, curr) => prev + (curr.dbTta?.pickupTransport?.quantityDecimal ?? 0), 0)}
          />
        ),
      }),
      getCompareColumn({
        title: t("table.entity.ticket.column.mileage", "Mileage"),
        accessor: "mileage",
        width: 100,
        isEqual: (record) => record.tta?.mileage === record.dbTta?.mileage,
        render: ({tta, dbTta}) => {
          return (
            <QuantityCell importValue={tta?.mileage} dbValue={dbTta?.mileage}/>
          )
        },
      }),
      getCompareColumn({
        title: t("table.entity.ticket.column.fuelsurchargecost", "Fuel Surcharge Cost"),
        accessor: "fuelSurchargeCost",
        width: 100,
        isEqual: (record) => record.tta?.fuelSurchargeCost === record.dbTta?.getFuelSurchargeCost("Hauling"),
        render: ({tta, dbTta}) => {
          return (
            <CurrencyCell importValue={tta?.fuelSurchargeCost} dbValue={dbTta?.getFuelSurchargeCost("Hauling")}/>
          )
        },
        footer: (
          <FormattedCurrency
            options={{
              ...options,
              signDisplay: "always"
            }}
            value={filteredRecords.reduce((prev, curr) => prev + (curr.tta?.fuelSurchargeCost ?? 0), 0) - filteredRecords.reduce((prev, curr) => prev + (curr.dbTta?.getFuelSurchargeCost("Hauling") ?? 0), 0)}
          />
        ),
      }),
      getCompareColumn({
        title: t("table.entity.ticket.column.demurragecost", "Demmurage Cost"),
        accessor: "demmurageCost",
        width: 100,
        isEqual: (record) => record.tta?.demmurageCost === record.dbTta?.getDemmurageCost("Hauling"),
        render: ({tta, dbTta}) => {
          return (
            <CurrencyCell importValue={tta?.demmurageCost} dbValue={dbTta?.getDemmurageCost("Hauling")}/>
          )
        },
        footer: (
          <FormattedCurrency
            options={{
              ...options,
              signDisplay: "always"
            }}
            value={filteredRecords.reduce((prev, curr) => prev + (curr.tta?.demmurageCost ?? 0), 0) - filteredRecords.reduce((prev, curr) => prev + (curr.dbTta?.getDemmurageCost("Hauling") ?? 0), 0)}
          />
        ),
      }),
      getCompareColumn({
        title: t("table.entity.ticket.column.rejectcost", "Reject Cost"),
        accessor: "rejectCost",
        width: 100,
        isEqual: (record) => record.tta?.rejectCost === record.dbTta?.getRejectCost("Hauling"),
        render: ({tta, dbTta}) => {
          return (
            <CurrencyCell importValue={tta?.rejectCost} dbValue={dbTta?.getRejectCost("Hauling")}/>
          )
        },
        footer: (
          <FormattedCurrency
            options={{
              ...options,
              signDisplay: "always"
            }}
            value={filteredRecords.reduce((prev, curr) => prev + (curr.tta?.rejectCost ?? 0), 0) - filteredRecords.reduce((prev, curr) => prev + (curr.dbTta?.getRejectCost("Hauling") ?? 0), 0)}
          />
        ),
      }),
      getCompareColumn({
        title: t("table.entity.ticket.column.splitloadcost", "Split Load Cost"),
        accessor: "splitLoadCost",
        width: 100,
        isEqual: (record) => record.tta?.splitLoadCost === record.dbTta?.getSplitLoadCost("Hauling"),
        render: ({tta, dbTta}) => {
          return (
            <CurrencyCell importValue={tta?.splitLoadCost} dbValue={dbTta?.getSplitLoadCost("Hauling")}/>
          )
        },
        footer: (
          <FormattedCurrency
            options={{
              ...options,
              signDisplay: "always"
            }}
            value={filteredRecords.reduce((prev, curr) => prev + (curr.tta?.splitLoadCost ?? 0), 0) - filteredRecords.reduce((prev, curr) => prev + (curr.dbTta?.getSplitLoadCost("Hauling") ?? 0), 0)}
          />
        ),
      }),
      getCompareColumn({
        title: t("table.entity.ticket.column.timecost", "Time Cost"),
        accessor: "timeCost",
        width: 100,
        isEqual: (record) => record.tta?.timeCost === record.dbTta?.getTimeCost("Hauling"),
        render: ({tta, dbTta}) => {
          return (
            <CurrencyCell importValue={tta?.timeCost} dbValue={dbTta?.getTimeCost("Hauling")}/>
          )
        },
        footer: (
          <FormattedCurrency
            options={{
              ...options,
              signDisplay: "always"
            }}
            value={filteredRecords.reduce((prev, curr) => prev + (curr.tta?.timeCost ?? 0), 0) - filteredRecords.reduce((prev, curr) => prev + (curr.dbTta?.getTimeCost("Hauling") ?? 0), 0)}
          />
        ),
      }),
      getCompareColumn({
        title: t("table.entity.ticket.column.freightcost", "Freight Cost"),
        accessor: "freightCost",
        width: 100,
        isEqual: (record) => record.tta?.freightCost === record.dbTta?.getFreightCost("Hauling"),
        render: ({tta, dbTta}) => {
          return (
            <CurrencyCell importValue={tta?.freightCost} dbValue={dbTta?.getFreightCost("Hauling")}/>
          )
        },
        footer: (
          <FormattedCurrency
            options={{
              ...options,
              signDisplay: "always"
            }}
            value={filteredRecords.reduce((prev, curr) => prev + (curr.tta?.freightCost ?? 0), 0) - filteredRecords.reduce((prev, curr) => prev + (curr.dbTta?.getFreightCost("Hauling") ?? 0), 0)}
          />
        ),
      }),
      getCompareColumn({
        title: t("table.entity.ticket.column.loadcost", "Load Cost"),
        accessor: "loadCost",
        width: 100,
        isEqual: (record) => record.tta?.loadCost === record.dbTta?.getLoadCost("Hauling"),
        render: ({tta, dbTta}) => {
          return (
            <CurrencyCell importValue={tta?.loadCost} dbValue={dbTta?.getLoadCost("Hauling")}/>
          )
        },
        footer: (
          <FormattedCurrency
            options={{
              ...options,
              signDisplay: "always"
            }}
            value={filteredRecords.reduce((prev, curr) => prev + (curr.tta?.loadCost ?? 0), 0) - filteredRecords.reduce((prev, curr) => prev + (curr.dbTta?.getLoadCost("Hauling") ?? 0), 0)}
          />
        ),
      }),
      getCompareColumn({
        title: t("table.entity.ticket.column.totalcost", "Total Cost"),
        accessor: "totalCost",
        width: 100,
        isEqual: (record) => record.tta?.totalCost === record.dbTta?.getTotalCost("Hauling"),
        render: ({tta, dbTta}) => {
          return (
            <CurrencyCell importValue={tta?.totalCost} dbValue={dbTta?.getTotalCost("Hauling")}/>
          )
        },
        footer: (
          <FormattedCurrency
            options={{
              ...options,
              signDisplay: "always"
            }}
            value={filteredRecords.reduce((prev, curr) => prev + (curr.tta?.totalCost ?? 0), 0) - filteredRecords.reduce((prev, curr) => prev + (curr.dbTta?.getTotalCost("Hauling") ?? 0), 0)}
          />
        ),
      }),
      {
        title: '',
        accessor: "actions",
        width: 50,
        render: (record, index) => {
          return (
            <Group spacing={4} position="right" noWrap>
              <ActionIcon color="primary" onClick={() => setCompare(record)}>
                <IconLayersDifference size={16}/>
              </ActionIcon>
            </Group>
          )
        }
      }
    ]
  }, [filterConfig, filteredRecords, t])

  return (
    <>
      <DataTable
        {...rest}
        {...(pagination ?? {})}
        sortStatus={sortStatus}
        onSortStatusChange={onSortStatusChange}
        columns={columns}
        scrollViewportRef={scrollViewportRef}
      />
      {compare &&
        <TicketCompareModal
          title={"Compare"}
          size="xl"
          opened={true}
          onClose={() => setCompare(undefined)}
          value={compare}/>
      }
    </>
  );
};
